import type { RelayEventTypes, RelayMessageHandler, Relays, Command, EncodedCommandType } from './types.ts';
import { IOGateway } from '@interopio/gateway';
import { EventEmitter } from 'node:events';

const codec = IOGateway.Encoding.transit<Command>({
    keywordize: new Map<string, IOGateway.Encoding.KeywordizeCommand>([
        ['/type', '*'],
        ['/message/body/type', '*'],
        ['/message/origin', '*'],
        ['/message/receiver/type', '*'],
        ['/message/source/type', '*'],
        ['/message/body/type', '*'],
    ])
});

// type guard, checks if the message is encoded (string or Buffer) the guard is set to string because Buffer can be used as string in JSON.parse()
function isEncoded(msg: EncodedCommandType | Command): msg is string {
    return typeof msg === 'string' || Buffer.isBuffer(msg);
}

type KeyedLinkData = {
    key: string; // relay key
    node: string; // node id
    linksByNode: Map<string, string>; // linked nodes -> sender key
}

export class InternalRelays implements Relays {
    readonly #logger: IOGateway.Logging.Logger;
    // key -> handler
    readonly #handlersByKey = new Map<string, RelayMessageHandler>();
    // node -> keyed link
    readonly #links = new Map<string, KeyedLinkData>();
    readonly #eventEmitter = new EventEmitter<RelayEventTypes>();

    constructor(logger: IOGateway.Logging.Logger) {
        this.#logger = logger;
    }

    add(key: string, handler: RelayMessageHandler) {
        this.#handlersByKey.set(key, handler);
    }

    remove(key: string) {
        this.#handlersByKey.delete(key);
        this.#disconnect(key);
    }

    #disconnect(key: string, from?: string) {
        for (const [node, k] of this.#links) {
            if (k.key === key && (from === undefined || k.node === from)) {
                const links = k.linksByNode;
                if (this.#logger.enabledFor('info')) {
                    this.#logger.info(`${key} unregisters node ${node}, linked to ${Array.from(links.keys()).join(', ')}`);
                }
                this.#links.delete(node);
                this.#eventEmitter.emit('disconnect', key, node, links);
            }
        }
    }
    #connect(key: string, node: string) {
        if (this.#logger.enabledFor('info')) {
            this.#logger.info(`${key} registers node ${node}`);
        }
        this.#links.set(node, {key, node: node, linksByNode: new Map<string, string>()});
        this.#eventEmitter.emit('connect', key, node);
    }

    receive(key: string, msg: string | Command) {
        const {node, decoded, encoded} = this.link(key, msg);

        if (node) {
            this.#eventEmitter.emit('message', key, node, encoded, decoded);
        }
    }

    private link(key: string, msg: string | Command) {
        try {
            const decoded: Command = isEncoded(msg) ? codec.decode(msg) : msg;
            const {type, from, to} = decoded;
            if (to === 'all') {
                switch (type) {
                    case 'hello': {
                        this.#connect(key, from);
                        break;
                    }
                    case 'bye': {
                        this.#disconnect(key, from);
                        break;
                    }
                }
                return { decoded };
            }
            else {
                const node = decoded.to;
                const link = decoded.from;
                const type = decoded.type;
                const senderLinkData = this.#links.get(link);
                switch (type) {
                    case 'hello':
                        if (this.#logger.enabledFor('debug')) {
                            this.#logger.debug(`${key} connecting ${link} to ${node}`);
                        }
                        senderLinkData?.linksByNode.set(decoded.to, key);
                        break;
                    case 'bye':
                        if (this.#logger.enabledFor('debug')) {
                            this.#logger.debug(`${key} disconnecting ${link} from ${node}`);
                        }
                        senderLinkData?.linksByNode.delete(decoded.to);
                        break;
                }
            }

            const encoded: string = isEncoded(msg) ? msg : codec.encode(msg);
            return { node: from, decoded, encoded };
        }
        catch (e) {
            // probably a decode error or a bug
            if (!this.#eventEmitter.emit('error', key, e instanceof Error ? e : new Error(`link failed :${e}`))) {
                this.#logger.warn(`${key} unable to process ${msg}`, e);
            }
        }
    }

    on<K extends keyof RelayEventTypes>(event: K, listener: (K extends keyof RelayEventTypes ? RelayEventTypes[K] extends unknown[] ? (...args: RelayEventTypes[K]) => void : never : never)): this {
        this.#eventEmitter.on(event, listener);
        return this;
    }

    send(key: string, node: string, msg: string | Command, cb: (k: string, err?: Error) => void): void {
        if (this.#logger.enabledFor('trace')) {
            this.#logger.debug(`${key} sending msg to ${node}`);
        }

        const encoded = isEncoded(msg) ? msg : codec.encode(msg);
        const decoded = isEncoded(msg) ? codec.decode(msg) : msg;
        {
            node ??= decoded.to;
            const link = decoded.from;
            const type = decoded.type;
            const senderLinkData = this.#links.get(link);
            switch (type) {
                case 'hello':
                    if (this.#logger.enabledFor('debug')) {
                        this.#logger.debug(`${key} connecting ${link} to ${node}`);
                    }
                    senderLinkData?.linksByNode.set(node, key);
                    break;
                case 'bye':
                    if (this.#logger.enabledFor('debug')) {
                        this.#logger.debug(`${key} disconnecting ${link} from ${node}`);
                    }
                    senderLinkData?.linksByNode.delete(node);
                    break;
            }
        }
        {
            const receiverLinkData = this.#links.get(node);
            if (receiverLinkData?.key) {
                const handler = this.#handlersByKey.get(receiverLinkData?.key);
                if (handler) {
                    handler(encoded, decoded, cb);
                    return;
                }
            }
        }
        if (decoded.type === 'bye') {
            return;
        }
        throw new Error(`${key} no active link for ${node}`);
    }
}
